import type { FilterSpecification, LayerSpecification } from "maplibre-gl";
import { SOURCES } from "../sources";
import { LAYERS } from "./constants";

// Safe expression builder that intercepts the "Deselect All" state before compiling WebGL rules
const getAircraftFilter = (): FilterSpecification => {
  if (typeof window === "undefined") return ["has", "track_id"];

  try {
    const saved = localStorage.getItem("skopa_map_filters_v3");
    const hiddenCells: string[] = saved ? JSON.parse(saved) : [];

    if (hiddenCells.length >= 35) {
      return ["==", ["get", "matrix_cell"], "force_hide_all_layer_assets"];
    }

    return [
      "let",
      "cell_key",
      [
        "coalesce",
        ["get", "matrix_cell"],
        [
          "concat",
          [
            "coalesce",
            ["get", "matrix_row"],
            ["get", "standard_identity"],
            "u",
          ],
          "-",
          ["coalesce", ["get", "matrix_col"], ["get", "environment"], "a"],
        ],
      ],
      [
        "let",
        "hidden_cache",
        ["literal", hiddenCells],
        ["!", ["in", ["var", "cell_key"], ["var", "hidden_cache"]]],
      ],
    ] as FilterSpecification;
  } catch (e) {
    console.error("Filter evaluation failed, defaulting to show all:", e);
    return ["has", "track_id"];
  }
};

export const aircraft: LayerSpecification = {
  id: LAYERS.AIRCRAFT,
  type: "symbol",
  source: SOURCES.AIRCRAFT,

  filter: [
    "all",
    // Checks if entity_type property is completely absent/null
    ["==", ["coalesce", ["get", "entity_type"], "not_set"], "not_set"],
    getAircraftFilter(),
  ] as unknown as FilterSpecification, // Force type assertion safety fallback

  layout: {
    "icon-image": ["coalesce", ["get", "icon"], "default"],
    "icon-rotate": ["coalesce", ["get", "heading_deg"], 0],
    "icon-rotation-alignment": "map",
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
    "symbol-sort-key": ["*", ["get", "altitude_ft"], -1],
    "icon-size": [
      "case",
      ["boolean", ["get", "is_selected"], false],
      0.45,
      0.35,
    ],
    "text-field": ["step", ["zoom"], "", 8, ["format", ["get", "label_text"]]],
    "text-size": [
      "interpolate",
      ["linear"],
      ["zoom"],
      8,
      11,
      12,
      12.5,
      16,
      14.5,
    ],
    "text-max-width": 12,
    "text-line-height": 1.05,
    "text-letter-spacing": 0.02,
    "text-font": ["IBM Plex Mono Regular"],
    "text-rotation-alignment": "map",
    "text-allow-overlap": true,
    "text-ignore-placement": true,
    "text-anchor": [
      "case",
      [">", ["coalesce", ["get", "heading_deg"], 0], 180],
      "left",
      "right",
    ],
    "text-justify": [
      "case",
      [">", ["coalesce", ["get", "heading_deg"], 0], 180],
      "left",
      "right",
    ],
    "text-radial-offset": 2.0,
  },
  paint: {
    "icon-color": [
      "match",
      ["coalesce", ["get", "matrix_row"], ["get", "standard_identity"], "u"],

      // Pending / Unknown (Rich, more saturated warm parchment/gold)
      "p",
      "#F5E5B8",
      "u",
      "#F5E5B8",

      // Friend / Future (Clearer, more saturated sky slate-blue)
      "f",
      "#C6E2FA",
      "future",
      "#C6E2FA",

      // Neutral (More defined, richer mint/sage green)
      "n",
      "#D7EBC4",

      // Suspect / Hostile (Bolder, deeper crimson/rose red)
      "s",
      "#E09895",
      "h",
      "#E09895",

      // Fallback (Pure white)
      "#FFFFFF",
    ],

    "icon-halo-width": 0.4,

    "text-color": [
      "case",
      ["boolean", ["get", "is_selected"], false],
      "#FFFFFF",
      "#BBBBBB",
    ],
    "text-halo-color": "#101010",
    "text-halo-width": 8,
    "text-halo-blur": 1.5,
  },
};
